import { useState, useEffect } from "react";
import { useAuthContext } from "../../contexts/AuthContext";
import { getUserPayments } from "../../services/paymentService";
import { Payment } from "../../types/payment";
import { CreditCard, Receipt, Calendar, DollarSign } from "lucide-react";

export default function PaymentHistory() {
  const { user } = useAuthContext();
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPayments = async () => {
      if (!user) return;

      try {
        const data = await getUserPayments(user.uid);
        setPayments(data);
      } catch (err) {
        console.error("Error fetching payments:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, [user]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "text-green-500 bg-green-50";
      case "pending":
        return "text-yellow-500 bg-yellow-50";
      case "failed":
        return "text-red-500 bg-red-50";
      case "refunded":
        return "text-gray-500 bg-gray-50";
      default:
        return "text-gray-500 bg-gray-50";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "สำเร็จ";
      case "pending":
        return "รอดำเนินการ";
      case "failed":
        return "ล้มเหลว";
      case "refunded":
        return "คืนเงิน";
      default:
        return status;
    }
  };

  const getMethodText = (method: string) => {
    switch (method) {
      case "credit_card":
        return "บัตรเครดิต/เดบิต";
      case "promptpay":
        return "PromptPay";
      case "bank_transfer":
        return "โอนเงินผ่านธนาคาร";
      default:
        return method;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-textLight">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50/20 to-pink-50/20 py-12 px-4">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl md:text-4xl font-bold text-textDark mb-8">
          ประวัติการชำระเงิน
        </h1>

        {payments.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-xl p-12 text-center">
            <div className="flex items-center justify-center mb-4">
              <CreditCard className="w-20 h-20 text-gray-300" />
            </div>
            <h2 className="text-2xl font-bold text-textDark mb-2">
              ยังไม่มีประวัติการชำระเงิน
            </h2>
            <p className="text-textLight">
              เมื่อคุณซื้อคอร์สหรือสมัคร Subscription จะแสดงที่นี่
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                      วันที่
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                      รายการ
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-textDark">
                      วิธีชำระเงิน
                    </th>
                    <th className="px-6 py-4 text-right text-sm font-semibold text-textDark">
                      จำนวนเงิน
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-textDark">
                      สถานะ
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 text-sm text-textDark">
                        {new Date(payment.createdAt).toLocaleDateString("th-TH", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-textDark">
                          {payment.description}
                        </div>
                        <div className="text-xs text-textLight">
                          ID: {payment.id.slice(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-textDark">
                        {getMethodText(payment.method)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="text-lg font-bold text-primary">
                          ฿{payment.amount.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            payment.status
                          )}`}
                        >
                          {getStatusText(payment.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary */}
        {payments.length > 0 && (
          <div className="mt-8 grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 text-sm text-textLight mb-2">
                <DollarSign className="w-4 h-4" />
                <span>ยอดรวมทั้งหมด</span>
              </div>
              <div className="text-3xl font-bold text-blue-600">
                ฿
                {payments
                  .filter((p) => p.status === "completed")
                  .reduce((sum, p) => sum + p.amount, 0)
                  .toLocaleString()}
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 text-sm text-textLight mb-2">
                <Receipt className="w-4 h-4" />
                <span>จำนวนรายการ</span>
              </div>
              <div className="text-3xl font-bold text-blue-600">
                {payments.filter((p) => p.status === "completed").length}
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg p-6">
              <div className="flex items-center gap-2 text-sm text-textLight mb-2">
                <Calendar className="w-4 h-4" />
                <span>รายการล่าสุด</span>
              </div>
              <div className="text-lg font-bold text-green-600">
                {payments.length > 0
                  ? new Date(payments[0].createdAt).toLocaleDateString("th-TH")
                  : "-"}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
